import {
  get,
  each,
  replace,
  isEmpty,
  size,
  find,
  forEach,
  omit,
  cloneDeep,
  isEqual,
  pick,
  keys,
  has,
  set,
} from 'lodash';
import ReactHtmlParser from 'react-html-parser';
import * as XLSX from 'xlsx';

import {
  PUNCHOUT_SETUP,
  FREIGHT_CHARGE_DETAILS,
  DEFAULT_FREIGHT_DETAILS,
  DISMISS_SYSTEM_NOTIFICATION_STORAGE,
  SETUP_FAILURE_STATUS_CODE,
  GST_BANNER_MESSAGE_LABEL,
} from '../global/constants';
import {
  sessionGetItem,
  storageClear,
  sessionGetItemValue,
  sessionSetItemValue,
  storageSessionClear,
} from './storage';
import { DEFAULT_REDUX_KEY } from '../lib/dynamicStore/constants';
import {
  punchoutSetupFailure,
  setEndSessionModalStatus,
  setGlobalErrorStatus,
  removeGlobalError,
} from '../global/actions';
import {
  BILLING_BLOCK_TAX_FILE,
  TAX_EXEMPTION_ATTACHMENT_TYPE,
  PURCHASE_ORDER_ATTACHMENT_TYPE,
  ADDRESS_TYPE,
} from '../components/templates/Checkout/Checkout.constants';

export const getStore = () => {
  if (window[DEFAULT_REDUX_KEY]) {
    return window[DEFAULT_REDUX_KEY].getState();
  }
  return {};
};

/**
 * Function to check for freightEnabled in session
 * @returns {Boolean} - defaiult: false
 */
export const isFreightEnabled = () => get(sessionGetItem(PUNCHOUT_SETUP), 'freightEnabled', false);

/**
 * Function to check default state for freight in session
 * @returns {String} - defaiult: blank string
 */
export const getFreightDefaultState = () => get(sessionGetItem(PUNCHOUT_SETUP), 'state', '');

/**
 * Function to check for selected carrierNumber in POSR session data
 * @returns {String} - default: blank string
 */
export const getSelectedCarrierNumber = () =>
  get(sessionGetItem(PUNCHOUT_SETUP), 'selectedCarrierNumber', '');

/**
 * Function to check for selected state in POSR session data
 * @returns {String} - default: blank string
 */
export const getSelectedState = () => get(sessionGetItem(PUNCHOUT_SETUP), 'selectedState', '');

/**
 * Function to get the default state value from session
 * @returns {String} - default: blank string
 */
export const getDefaultState = () =>
  get(sessionGetItem(DEFAULT_FREIGHT_DETAILS), 'defaultState', '');

/**
 * Function to get the default carrierNumber from session
 * @returns {String} - default: blank string
 */
export const getDefaultCarrierNumber = () =>
  get(sessionGetItem(DEFAULT_FREIGHT_DETAILS), 'defaultCarrierNumber', '');

/**
 * Function to get state and carrierInfo from session
 * @returns {Object} - default: empty Object
 */
export const getShippingInfo = () => {
  const freightChargeDetails = sessionGetItem(FREIGHT_CHARGE_DETAILS);
  const carrierNumber = get(
    freightChargeDetails,
    'shippingCarrier.carrierNumber',
    getSelectedCarrierNumber() || getDefaultCarrierNumber()
  );
  const state = get(
    freightChargeDetails,
    'state',
    getSelectedState() || getFreightDefaultState() || getDefaultState()
  );
  const response = {};
  if (carrierNumber) {
    response.carrierNumber = carrierNumber;
  }
  if (state) {
    response.state = state;
  }
  return response;
};

/**
 * Function to get the selcted ShipmentMethod from session
 * @param {Array} shippingMethods
 * @returns {String} - default: blank string
 */
export const selectedShipmentMethod = shippingMethods => {
  const { carrierNumber } = getShippingInfo();
  return carrierNumber || get(shippingMethods, ['0', 'value'], '');
};

/**
 * Function to get the default/selcted state from session
 * @returns {String} - default: blank string
 */
export const selectedShipmentState = () => {
  const { state } = getShippingInfo();
  return state || '';
};

/**
 * Function to replace all occurrences
 * @param {arrData} - Array
 * @param {resource} - string
 * @returns {String}
 */
export const replaceAll = (arrData, resource = '') => {
  let response = resource;
  each(arrData, (value, key) => {
    response = replace(response, `{${key}}`, value);
  });
  return response;
};

export const getCompanyLogo = (dir, company) => {
  const logoDir = dir.replace(/\/$/, '');
  const logo = company
    .replace(/[#%{}?&-]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase();
  return `${logoDir}/${logo}.png`;
};

export const htmlParser = (text = '') => {
  try {
    return ReactHtmlParser(text.trim()).toString();
  } catch (err) {
    return text;
  }
};

export const getOrCondition = (fistValue, secondValue) => fistValue || secondValue;

export const endProcurementSession = () => {
  const punchoutSession = sessionGetItem(PUNCHOUT_SETUP);
  if (punchoutSession) {
    const { redirectUrl } = punchoutSession;
    const systemNotificationStatus = sessionGetItemValue(DISMISS_SYSTEM_NOTIFICATION_STORAGE);
    storageSessionClear();
    storageClear();
    if (!isEmpty(systemNotificationStatus)) {
      sessionSetItemValue(DISMISS_SYSTEM_NOTIFICATION_STORAGE, systemNotificationStatus);
    }
    window.location.href = redirectUrl;
  } else if (window[DEFAULT_REDUX_KEY]) {
    const data = { status: true, code: SETUP_FAILURE_STATUS_CODE };
    window[DEFAULT_REDUX_KEY].dispatch(setEndSessionModalStatus(false));
    window[DEFAULT_REDUX_KEY].dispatch(punchoutSetupFailure(data));
  }
};

export const downloadExcelSheet = (data, header, fileName) => {
  const sheet = XLSX.utils.json_to_sheet(data, {
    header,
  });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet);
  XLSX.writeFile(workbook, fileName);
};

export const isErrorCodeExistInLabel = (labels, errors) =>
  !!(!isEmpty(errors) && !isEmpty(labels) && labels[errors.code]);

export const validateAddress = (address, addressStatus) => {
  let status = addressStatus;
  if (size(address) > 0 && !status) {
    let selectedAddress = find(address, item => item && item.isDefault === true);
    selectedAddress = selectedAddress || address.slice(0, 1)[0];
    if (!isEmpty(selectedAddress)) {
      const { gstNumber = '' } = selectedAddress;
      if (isEmpty(gstNumber)) {
        status = true;
      }
    }
  }
  return status;
};

export const GSTValidateInAddress = (address = [], isGSTEnabled = false) => {
  if (isGSTEnabled && size(address) > 0) {
    const { shipToInfo = [], billToInfo = [] } = address;
    let status = false;
    status = validateAddress(shipToInfo, status);
    status = validateAddress(billToInfo, status);
    let error = { code: GST_BANNER_MESSAGE_LABEL };
    if (status) {
      window[DEFAULT_REDUX_KEY].dispatch(setGlobalErrorStatus({ error }));
    } else {
      error = {
        ...error,
        page: window.location.pathname,
      };
      window[DEFAULT_REDUX_KEY].dispatch(removeGlobalError({ ...error }));
    }
  }
};

export const getGSTBannerMessageStatus = isGSTEnabled => {
  let status = false;
  if (isGSTEnabled) {
    const store = getStore();
    const error = get(store, ['global', 'globalData', 'error'], []);
    const errorStatus = find(
      error,
      item => item.code === GST_BANNER_MESSAGE_LABEL && item.page === window.location.pathname
    );
    status = !isEmpty(errorStatus);
  }
  return status;
};

export const getDefaultPartner = (addressInfo, defaultSoldTo) => {
  const defaultAddress =
    addressInfo.find(item => item.isDefault === true) ||
    (addressInfo[0].addressChangeType !== 'NEW' && addressInfo[0]);
  return defaultAddress?.partnerNumber || defaultSoldTo;
};

// Concatenate departmentLab and departmentLab2 with pipe separator
export const combinedDepartmentLab = (departmentLab, departmentLab2) =>
  [departmentLab, departmentLab2].filter(val => val && val.trim()).join(' | ');

export const getBogoProductStatus = () => {
  const store = getStore();
  const entries = get(store, ['global', 'globalData', 'cart', 'entries'], []);
  const coupons = get(store, ['global', 'globalData', 'cart', 'coupons'], []);
  let isBogoItemPresent = false;
  if (!isEmpty(coupons) && !isEmpty(entries)) {
    forEach(entries, item => {
      const { isFreeGift, isFreeProduct } = item;
      isBogoItemPresent = isFreeGift || isFreeProduct;
      return !isBogoItemPresent;
    });
  }
  return isBogoItemPresent;
};

export const getAndCondition = (fistValue, secondValue) => fistValue && secondValue;

export const getSoldToData = customers => {
  const soldToArr = [];
  customers.forEach(item => {
    const { soldToInfo, soldToFlag, name, customerNumber, salesOrg, displayCustomerNumber } = item;
    soldToArr.push({
      soldToInfo,
      soldToFlag,
      name,
      salesOrg,
      customerNumber,
      displayCustomerNumber,
    });
  });
  return soldToArr;
};
export const getCustomerPriceValue = (productItem = {}, includeTaxWithPrice = false) => {
  const { customerPrice = {}, customerPriceWithTax = {} } = productItem;
  const { formattedValue } = customerPriceWithTax;
  if (includeTaxWithPrice && typeof formattedValue !== 'undefined') {
    return customerPriceWithTax;
  }
  return customerPrice;
};

export const getBasePriceValue = (productItem = {}, includeTaxWithPrice = false) => {
  const { basePrice = {}, basePriceWithTax = {} } = productItem;
  const { formattedValue } = basePriceWithTax;
  if (includeTaxWithPrice && typeof formattedValue !== 'undefined') {
    return basePriceWithTax;
  }
  return basePrice;
};

export const getFinalPriceValue = (productItem, includeTaxWithPrice) => {
  const basePriceValue = getBasePriceValue(productItem, includeTaxWithPrice);
  const customerPriceValue = getCustomerPriceValue(productItem, includeTaxWithPrice);
  return customerPriceValue?.formattedValue || basePriceValue?.formattedValue || '';
};

export const getTernaryCondition = (condition, exprIfTrue, exprIfFalse) =>
  condition ? exprIfTrue : exprIfFalse;

export const getProductList = products => {
  const productList = [];
  if (!isEmpty(products)) {
    forEach(products, value => {
      const {
        quantity,
        product: { code, name, hsnCode },
        isFreeGift,
        isFreeProduct,
        itemNumber,
        relatedEntryForDiscount,
      } = value;
      productList.push({
        number: code,
        quantity,
        name,
        hsnCode,
        isFreeGift,
        isFreeProduct,
        itemNumber,
        relatedEntryForDiscount,
      });
    });
  }
  return productList;
};

export const getEmployeeSource = props => {
  const {
    userDetails: { employeeFlag, employeeType, employeeCostCenter, urouser },
  } = props;
  const userDetails = get(props, 'checkoutData.userDetails', {});
  let employeeData = { employeeFlag };
  if (urouser) {
    if (employeeType) {
      employeeData = { ...employeeData, employeeType };
    }
    if (employeeCostCenter) {
      employeeData = { ...employeeData, employeeCostCenter };
    }
  } else {
    employeeData = {
      employeeFlag: userDetails.employeeFlag,
      employeeType: userDetails.employeeType,
      employeeCostCenter: userDetails.employeeCostCenter,
    };
  }
  return employeeData;
};

export const getPOTaxFileCount = (attachmentDetails = []) => {
  let PurchaseOrderFile = 0;
  let TaxCertificateFile = 0;
  if (!isEmpty(attachmentDetails)) {
    forEach(attachmentDetails, value => {
      const attachmentType = get(value, 'attachmentType', '');
      if (attachmentType === TAX_EXEMPTION_ATTACHMENT_TYPE) {
        TaxCertificateFile = 1;
      }
      if (attachmentType === PURCHASE_ORDER_ATTACHMENT_TYPE) {
        PurchaseOrderFile = 1;
      }
    });
  }
  return { PurchaseOrderFile, TaxCertificateFile };
};

export const getShippingPaymentData = props => {
  const { body } = props;
  const {
    shipToInfo,
    billToInfo,
    purchaseOrderNumber,
    cardDetails,
    carrierInfo,
    shippingInstructions,
    orderSimulate,
    attentionTo,
    quoteNumber,
  } = body;
  const attachmentDetails = getPOTaxFileCount(get(body, ['attachmentDetails'], []));
  const { PurchaseOrderFile, TaxCertificateFile } = attachmentDetails;
  const shippingAndPaymentObj = {
    shipTo: shipToInfo,
    billTo: billToInfo,
    shipmentMethod: carrierInfo.name,
    poNumber: purchaseOrderNumber,
    cardDetails,
    shippingInstructions,
    orderSimulate,
    attentionTo,
    PurchaseOrderFile,
    TaxCertificateFile,
    quoteId: quoteNumber,
  };
  if (TaxCertificateFile) {
    shippingAndPaymentObj.billingBlock = BILLING_BLOCK_TAX_FILE;
  }
  return shippingAndPaymentObj;
};

export const getShareOrderAddress = address => {
  const updatedAddress = { ...address };
  updatedAddress.partnerNumber = undefined;
  return updatedAddress;
};

export const getShareOrderCartData = shoppingCartItems => {
  const getSkuPageUrl = pageUrl => {
    if (pageUrl) {
      const {
        location: { origin },
      } = window;
      return `${origin}${pageUrl}`;
    }
    return '';
  };
  const ShoppingCartData = cloneDeep(shoppingCartItems);
  return ShoppingCartData?.map(product => {
    const {
      quantity,
      customerPrice,
      customerPriceWithTax,
      product: { code, name, hsnCode, sKUPageUrl },
    } = product;
    const productData = { code, name, hsnCode, sKUPageUrl: getSkuPageUrl(sKUPageUrl) };
    return { quantity, customerPrice, customerPriceWithTax, product: productData };
  });
};

export const matchListItems = (pricesAPIResults, productData) => {
  const skusPrice = [];
  const store = getStore();
  const isIncludedTaxWithPrice = get(
    store,
    ['global', 'globalData', 'countryConfig', 'includeTaxWithPrice'],
    false
  );
  pricesAPIResults.forEach(pricesAPIResult => {
    const { errors = [] } = pricesAPIResult;
    if (size(errors) === 0) {
      const skuItem = {
        code: pricesAPIResult.productNumber,
        addToCartDisabled: pricesAPIResult.addToCartDisabled,
        purchasable: pricesAPIResult.purchasable,
        isLoading: false,
      };
      // Net Price doesn't exist for Unauthenticated Users
      if (pricesAPIResult.netPrice) {
        skuItem.custPrice = getTernaryCondition(
          isIncludedTaxWithPrice,
          pricesAPIResult.netPriceWithTax.formattedValue,
          pricesAPIResult.netPrice.formattedValue
        );
        skuItem.custValue = getTernaryCondition(
          isIncludedTaxWithPrice,
          pricesAPIResult.netPriceWithTax.value,
          pricesAPIResult.netPrice.value
        );
      } else {
        skuItem.custPrice = '';
        skuItem.custValue = '';
      }

      skuItem.listPrice = getTernaryCondition(
        isIncludedTaxWithPrice,
        pricesAPIResult?.basePriceWithTax?.formattedValue,
        pricesAPIResult?.basePrice?.formattedValue || ''
      );
      skuItem.listValue = getTernaryCondition(
        isIncludedTaxWithPrice,
        pricesAPIResult?.basePriceWithTax?.value,
        pricesAPIResult?.basePrice?.value || ''
      );
      let productItem = find(productData, item => item.id === pricesAPIResult.productNumber);
      productItem = { ...productItem, isLoading: false };
      // Using Base Price Currency Code because Net Price doesn't exist for Unauthenticated Users
      skuItem.currencyCode = pricesAPIResult?.basePrice?.currencyCode || '';

      skusPrice.push({ ...skuItem, ...productItem });
    }
  });
  return skusPrice;
};

export const getProductRecommendationAnalyticsData = (data, pageName) => {
  const sku = get(data, 'productSKU', '');
  const store = getStore();
  const productRecommendationData = get(
    store,
    ['global', 'globalData', 'productRecommendationData'],
    []
  );
  const selectedProduct = find(productRecommendationData, item => item.id === sku);
  let cartContext = '';
  if (!isEmpty(selectedProduct)) {
    const criteriaTitle = get(selectedProduct, ['activityDetails1', 'criteria.title'], '');
    const campaignName = get(selectedProduct, ['activityDetails1', 'campaign.recipe.name'], '');
    cartContext = `PR:${pageName}:${criteriaTitle}:${campaignName}`;
  }
  return { sku, cartContext };
};
export const setWithExpiry = (key, value, ttl) => {
  const now = new Date();
  const item = {
    value,
    timestamp: now.getTime() + ttl,
  };
  sessionStorage.setItem(key, JSON.stringify(item));
};

export const getWithExpiry = key => {
  const itemStr = sessionStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();
  if (now.getTime() > item.timestamp) {
    sessionStorage.removeItem(key);
    return null;
  }
  return item.value;
};

export const compareAddressValues = (prevAddress, newAddress) => {
  const addressKeys = ['address1', 'address2', 'address3', 'street2'];

  const checkAndAddAddressKeys = addressObject => {
    addressKeys.forEach(key => {
      if (!has(addressObject, key)) {
        set(addressObject, key, undefined);
      }
    });
    return addressObject;
  };
  const completePrevAddress = checkAndAddAddressKeys(cloneDeep(prevAddress));
  const filteredNewAdd = pick(newAddress, keys(completePrevAddress));
  const excludedNewObject = omit(filteredNewAdd, ['stateName', 'countryName']);
  const excludedPrevObject = pick(completePrevAddress, keys(excludedNewObject));
  return isEqual(excludedPrevObject, excludedNewObject);
};

export const getSoldToAddress = (userDetails, soldTo) => {
  const dummySoldToId = get(userDetails, 'dummySoldto');
  const isUROUser = get(userDetails, 'urouser');
  return isUROUser ? size(soldTo) > 0 : size(soldTo) > 0 && isEmpty(dummySoldToId);
};
// Flatten shipToInfo to include endUserAccounts
export const flattenAddresses = addresses => {
  const allAddresses = [];
  addresses.forEach(address => {
    // Remove endUserAccounts property from the parent address
    const { endUserAccounts, ...addressWithoutEndUsers } = address;
    allAddresses.push(addressWithoutEndUsers);
    // Add end user accounts as separate entries
    if (endUserAccounts && Array.isArray(endUserAccounts)) {
      endUserAccounts.forEach(endUserAccount => {
        allAddresses.push(endUserAccount);
      });
    }
  });
  return allAddresses;
};

export const mapAddressFields = (addresses, addressType) => {
  if (!addresses || isEmpty(addresses) || !addressType) return addresses;
  const allAddresses = [];
  addresses.forEach(address => {
    const mappedAddress =
      addressType === ADDRESS_TYPE.SHIPPING
        ? {
            ...address,
            address1: address?.companyExt1 || '',
            departmentLab: address?.companyExt2 || '',
          }
        : {
            ...address,
            address1: address?.companyExt1 || '',
            address2: address?.companyExt2 || '',
            address3: address?.companyExt3 || '',
          };

    allAddresses.push(mappedAddress);
  });
  return allAddresses;
};
